/*
 * Website Analytics — data layer (PlunkDocs specs/WEB_ACTIVITY_DASHBOARD_2026-09-28.md §3, §5).
 *
 * Phase 1 endpoints (all live) plus the phase-2 reads (series, channels, devices, page/flow, landing):
 *   /v1/activity/dashboard/{range}            totals (ActivityTotal) + daily performance rows
 *   /v1/site/kpis?from&to                     bounce · avg visit · new/returning (monthly, plunk|ga4)
 *   /v1/activity/metrics/page|product/{range} OData lists
 *   /v1/activity/metrics/location|place/{range}/{groupBy}
 *   /v1/activity/metrics/utm/{range}          source/medium/campaign rollup (channels derived here)
 *   /v1/activity/metrics/conversions/{range}  [{title, count}]
 *   /v1/activity/metrics/date/{range}         {startDate, endDate}
 *   /v1/activity/hosts/{range}                site chip
 *   /v1/activity/funnels + {id}/results/{range}
 *
 * Comparison: the dashboard endpoint has no comparison param, so the previous
 * window is the matching "last_*" preset (this_month → last_month …). Ranges
 * without a matching preset show no delta — never a faked one.
 */
import ServiceFactory from "../../service";
export const PAGE_SIZE_TOP = 8;
function svc(api, type = "activity") {
  const S = ServiceFactory.getType(api, type);
  return new S();
}
export async function safe(promise) {
  try {
    return await promise;
  } catch (e) {
    return null;
  }
}

// this_month → last_month etc. Custom / all_time / last_* → null (no comparison).
export function previousPreset(range) {
  const map = {
    today: "yesterday",
    this_week: "last_week",
    this_month: "last_month",
    this_quarter: "last_quarter",
    this_year: "last_year"
  };
  return map[range] || null;
}
export function isCustomRange(range) {
  return /^custom_\d{8}_\d{8}$/.test(range || "");
}

// ---------------------------------------------------------------- fetchers
export function getDashboard(range) {
  return svc("/dashboard").getAsync({
    id: range
  });
}
// Pages ribbon totals: the same scorecard the legacy Pages tab reads (totalPages, views,
// sessions, uniqueUsers, avgTimeSpan). Summing a top-N row list under-counts.
export function getPageScorecard(range) {
  return svc(`/metrics/page/scorecard/${range}`).getAsync();
}
// ---- Phase 2 (ActivityService WebActivityMetricsService; spec §5 asks 6.1-6.6) ----
// 6.1 + 6.5 + 6.6: {bucket, startDate, endDate, rollupAt, totals{pageViews, sessions, users,
// uniqueUsers, newUsers, returningUsers, conversions, conversionsValue, engagements,
// avgSessionSeconds, bounceRate}, points[{date, pageViews, sessions, users, newUsers,
// bounceSessions, conversions, conversionsValue, engagements, avgSessionSeconds}]}
export function getSeries(range, bucket, scope) {
  const q = [bucket ? `bucket=${bucket}` : "", scope ? `scope=${scope}` : ""].filter(Boolean).join("&");
  return svc(`/metrics/series/${range}${q ? `?${q}` : ""}`).getAsync();
}
// 6.2: {sessions, truncated, rows[{channel, source, medium, sessions, users, conversions, value}]}
export function getChannels(range) {
  return svc(`/metrics/channels/${range}`).getAsync();
}
// 6.3: {sessions, truncated, deviceTypes[], browsers[], os[]} rows = {name, sessions, users, share}
export function getDevices(range) {
  return svc(`/metrics/devices/${range}`).getAsync();
}
// 6.4a: {rows[{path, views, sessions, entrances, exits, exitRate}]}
export function getPageFlow(range) {
  return svc(`/metrics/page/flow/${range}`).getAsync();
}
// 6.4b: {rows[{source, medium, path, sessions}]}
export function getLanding(range) {
  return svc(`/metrics/landing/${range}`).getAsync();
}
export const CHANNEL_LABELS = {
  organic_search: "Organic search",
  direct: "Direct",
  paid: "Paid",
  social: "Social",
  referral: "Referral",
  email: "Email",
  ai: "AI assistants",
  other: "Other"
};
export function channelLabel(key) {
  return CHANNEL_LABELS[key] || key || "Other";
}
// Server channel rows → the same shape channelsFromUtm returns (one row per channel, sessions desc).
export function channelsFromServer(breakdown) {
  const acc = {};
  (breakdown && breakdown.rows || []).forEach(r => {
    const c = channelLabel(r.channel);
    const a = acc[c] || (acc[c] = {
      channel: c,
      key: r.channel,
      sessions: 0,
      users: 0,
      conversions: 0,
      engagements: 0,
      value: 0,
      sources: []
    });
    a.sessions += Number(r.sessions) || 0;
    a.users += Number(r.users) || 0;
    a.conversions += Number(r.conversions) || 0;
    a.engagements += Number(r.engagements) || 0;
    a.value += Number(r.value) || 0;
    a.sources.push(r);
  });
  return Object.values(acc).filter(x => x.sessions > 0).sort((a, b) => b.sessions - a.sessions);
}
// Landing rows → { "source|medium": topPath }
export function landingIndex(landing) {
  const idx = {};
  (landing && landing.rows || []).forEach(r => {
    const k = `${(r.source || "").toLowerCase()}|${(r.medium || "").toLowerCase()}`;
    if (!idx[k] || Number(r.sessions) > idx[k].sessions) idx[k] = {
      path: r.path,
      sessions: Number(r.sessions) || 0
    };
  });
  return idx;
}
// A comparison series only counts when it covers (nearly) its whole window: the Plunk
// workspace's tracker history starts 2026-08-24, so "last month" would otherwise be a
// half-empty window and every delta a fiction.
export function seriesCoverage(series) {
  if (!series || !series.points || !series.points.length || !series.startDate || !series.endDate) return 0;
  const s = new Date(series.startDate),
    e = new Date(series.endDate);
  const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
  if (series.bucket === "day") return Math.min(1, series.points.length / days);
  const first = new Date(series.points[0].date);
  return Math.min(1, 1 - Math.max(0, (first - s) / 86400000) / days);
}
export function seriesRows(series) {
  return (series && series.points || []).map(p => ({
    date: p.date,
    views: Number(p.pageViews) || 0,
    sessions: Number(p.sessions) || 0,
    users: Number(p.users) || 0,
    conversions: Number(p.conversions) || 0
  }));
}

// Legacy Activity tab data: {activityMetrics{conversionMetrics, engagementMetrics, conversionList, engagementList}, activity{items[{title,type,total,users,sessions,value}]}}
export async function getActivityData(range) {
  const r = await svc(`/metrics/data/${range}`).paginate({
    baseUrl: "",
    rawUrl: "",
    page: 1,
    pageSize: 200
  }).getAsync();
  const d = r && r.items ? r.items : r;
  if (!d || !d.activityMetrics) return null;
  const act = d.activity && Array.isArray(d.activity.items) ? d.activity.items : Array.isArray(d.activity) ? d.activity : [];
  return {
    metrics: d.activityMetrics,
    rows: act
  };
}
export function getWindow(range) {
  // {startDate, endDate} for the token (custom tokens resolve server-side too)
  return svc(`/metrics/date/${range}`).getAsync();
}
export function getConversions(range) {
  return svc(`/metrics/conversions/${range}`).getAsync();
}
export function getHosts(range) {
  return svc(`/hosts/${range}`).getAsync();
}
export function getHostPages(range, host) {
  return svc(`/hosts/pages/${range}?host=${encodeURIComponent(host)}&top=500`).getAsync();
}
export function getFunnels() {
  return svc("/funnels").getAsync();
}
export function getFunnelResults(id, range) {
  return svc(`/funnels/${id}/results/${range}`).getAsync();
}
function odata(api, {
  top = PAGE_SIZE_TOP,
  orderByDesc = "Views",
  page = 1
} = {}) {
  return svc(api).paginate({
    baseUrl: "",
    rawUrl: "",
    page,
    pageSize: top
  }).orderByDesc(orderByDesc).getAsync();
}
// Page titles are whatever the site put in <title>. Some templates emit an
// empty name plus the site suffix (" | Brand"), and quote-wrapped values arrive
// with their quotes doubled ("""...""") — show a readable title, or let the
// row fall back to its URL when nothing but the site suffix is left.
export function cleanPageTitle(t) {
  let s = String(t == null ? "" : t).trim();
  if (/^"/.test(s) && /"$/.test(s) && s.length > 1) s = s.replace(/^"+|"+$/g, "").trim();
  if (/^\|/.test(s)) return "";
  return s;
}
export async function getPages(range, opts) {
  const r = await odata(`/metrics/page/${range}`, opts);
  if (!(r && r.items)) return {
    items: [],
    pagination: null
  };
  r.items.forEach(p => {
    if (p && p.title != null) p.title = cleanPageTitle(p.title);
  });
  return r;
}
export async function getProducts(range, opts) {
  const r = await odata(`/metrics/product/${range}`, opts);
  return r && r.items ? r : {
    items: [],
    pagination: null
  };
}
export async function getLocations(range, groupBy = "city", opts) {
  const r = await odata(`/metrics/location/${range}/${groupBy}`, {
    orderByDesc: "Sessions",
    top: 60,
    ...(opts || {})
  });
  return r && r.items ? r : {
    items: [],
    pagination: null
  };
}
export async function getPlaces(range, opts) {
  const r = await odata(`/metrics/place/${range}/name`, {
    orderByDesc: "Sessions",
    top: 60,
    ...(opts || {})
  });
  return r && r.items ? r : {
    items: [],
    pagination: null
  };
}
export async function getUtm(range, opts) {
  const r = await odata(`/metrics/utm/${range}`, {
    orderByDesc: "Sessions",
    top: 200,
    ...(opts || {})
  });
  return r && r.items ? r : {
    items: [],
    pagination: null
  };
}

// Site KPIs are monthly; ask for the months the window touches.
export async function getSiteKpis(startDate, endDate) {
  if (!startDate || !endDate) return null;
  const ym = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  const s = new Date(startDate),
    e = new Date(endDate);
  if (isNaN(s) || isNaN(e)) return null;
  return svc(`/site/kpis?from=${ym(s)}&to=${ym(e)}`, "product").getAsync();
}

// ---------------------------------------------------------------- derivations
// Channel buckets from the UTM rollup (medium first, then the source host).
const SOCIAL = /facebook|instagram|pinterest|linkedin|tiktok|twitter|x\.com|youtube|reddit|threads|snapchat/i;
const SEARCH = /google|bing|duckduckgo|yahoo|baidu|yandex|ecosia/i;
const AI = /chatgpt|openai|perplexity|claude|anthropic|gemini|copilot|bard/i;
export function channelOf(row) {
  const medium = String(row.medium || "").toLowerCase();
  const source = String(row.source || "").toLowerCase();
  if (!source && !medium) return "Direct";
  if (source === "(direct)" || medium === "(none)") return "Direct";
  if (/cpc|ppc|paid|display|retarget|cpm|ads?$/.test(medium)) return "Paid";
  if (/email|newsletter/.test(medium)) return "Email";
  if (/social/.test(medium) || SOCIAL.test(source)) return "Social";
  if (/organic/.test(medium) || SEARCH.test(source)) return "Organic search";
  if (AI.test(source)) return "AI assistants";
  if (/referral/.test(medium) || source) return "Referral";
  return "Other";
}
export const CHANNEL_ORDER = ["Organic search", "Direct", "Paid", "Social", "Referral", "Email", "AI assistants", "Other"];
export function channelsFromUtm(items) {
  const acc = {};
  (items || []).forEach(r => {
    const c = channelOf(r);
    const a = acc[c] || (acc[c] = {
      channel: c,
      sessions: 0,
      users: 0,
      conversions: 0,
      engagements: 0
    });
    a.sessions += Number(r.sessions) || 0;
    a.users += Number(r.users) || 0;
    a.conversions += Number(r.conversions) || 0;
    a.engagements += Number(r.engagements) || 0;
  });
  return CHANNEL_ORDER.map(c => acc[c]).filter(x => x && x.sessions > 0).sort((a, b) => b.sessions - a.sessions);
}

// performance[] = [header, unitRow, [date, impressions, conversions, interactions]…]
export function dailyRows(performance) {
  return (performance || []).slice(2).map(r => ({
    date: r[0],
    views: Number(r[1]) || 0,
    conversions: Number(r[2]) || 0,
    interactions: Number(r[3]) || 0
  })).filter(r => r.date);
}

// Pick the site-KPI month(s) the window touches; prefer the tracker ('plunk') row.
export function siteKpiSummary(kpis, startDate, endDate) {
  if (!kpis || !Array.isArray(kpis.months) || !kpis.months.length) return null;
  const rows = kpis.months.filter(m => m && m.sessions > 0);
  if (!rows.length) return null;
  const totalSessions = rows.reduce((a, m) => a + (Number(m.sessions) || 0), 0);
  const w = k => rows.reduce((a, m) => a + (Number(m[k]) || 0) * (Number(m.sessions) || 0), 0) / (totalSessions || 1);
  const users = rows.reduce((a, m) => a + (Number(m.totalUsers) || 0), 0);
  const newUsers = rows.reduce((a, m) => a + (Number(m.newUsers) || 0), 0);
  return {
    months: rows.map(m => m.month),
    sources: Array.from(new Set(rows.map(m => m.source))),
    avgVisitSeconds: w("avgVisitSeconds"),
    bounceRate: w("bounceRate"),
    newShare: users ? newUsers / users : null,
    note: kpis.note || "",
    partial: rows.length !== kpis.months.length
  };
}
export function fmtDuration(seconds) {
  const s = Math.max(0, Math.round(Number(seconds) || 0));
  return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`;
}
export function pctOf(a, b) {
  a = Number(a) || 0;
  b = Number(b) || 0;
  return b ? a / b * 100 : 0;
}